import {
  Button,
  HStack,
  Icon,
  Input,
  Text,
  VStack,
  Center,
  Heading,
  Flex,
  RadioGroup,
  FormLabel,
  Radio,
  Stack,
  useToast,
  AlertIcon,
  Alert,
  Link,
} from '@chakra-ui/react'
import { useEffect, useState } from 'react'
import useApi from '../../common/utils/api/useApi'
import { InfoIcon, Search2Icon } from '@chakra-ui/icons'
import { Organization } from '../../types'
import { routes } from '../routes'
import { getOrganizationsByHPIO } from '../OrganizationProfilePage/OrganizationSlice'
import { Link as RLink, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { RootState } from '../../app/store'
import Page from '../../components/Page'
import ClickableTooltip from '../../components/shared-components/ClickableTooltip'
import { Logo } from '../../components/shared-components/Logo'
import { sendJoinOrganizationRequest } from '../../features/organization-membership/OrgMembershipSlice'
import { UserType } from '../../features/onboardingContext/OnboardingContextSlice'

const HPIO_NUMBER_HINT =
  'HPI-O is a unique 16-digit identifier for your organisation. You will need to ensure your organisation’s HPI-O is linked to RxPad’s CSP Number, otherwise electronic prescribing will not work.  Click here to find out how you can link your HPI-O to the RxPad CSP.'

const OrganizationLinkPage = () => {
  const [hpioQuery, setHpioQuery] = useState('')
  const [organizations, setOrganizations] = useState<Organization[] | null>()
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>()
  const navigate = useNavigate()
  const toast = useToast()

  const { selectedRole } = useSelector((state: RootState) => state.onboardingContext)

  useEffect(() => {
    if (!selectedRole) {
      navigate(routes.selectRoles.path)
    }
  }, [selectedRole])

  const {
    trigger: triggerGetOrganizationsByHPIO,
    loading: searchLoading,
    completed: searchCompleted,
  } = useApi(getOrganizationsByHPIO, {
    onSuccess: (organizations) => {
      setOrganizations(organizations)
    },
    onFailure: (error) => {
      setOrganizations(null)
      if (error.response.data.message === 'Clinic not found') {
        toast({
          title: 'HPI-O not found in RxPad. Please try again.',
          status: 'error',
          duration: 5000,
        })
      }
    },
  })

  const { trigger: triggerLinkOrganization, loading: linkLoading } = useApi(
    sendJoinOrganizationRequest,
    {
      onSuccess: async () => {
        const selectedOrg = organizations?.find((org) => org.id === selectedOrgId)
        navigate(routes.organizationJoinResponse.path, {
          state: {
            organizationName: selectedOrg?.name,
            hpio: selectedOrg?.clinic.hpio_number,
          },
        })
      },
      onFailure: (error) => {
        toast({
          title: error.response.data.message ?? 'Failed to join organisation. Please try again.',
          status: 'error',
          duration: 5000,
        })
      },
    }
  )

  const fetchSearchResult = async () => {
    if (!hpioQuery) return
    await triggerGetOrganizationsByHPIO(hpioQuery.trim())
  }

  const linkOrgToClinic = () => {
    if (!selectedOrgId || !selectedRole) return
    triggerLinkOrganization({
      organizationId: selectedOrgId,
      requestedRole: selectedRole as UserType,
    })
  }

  const handleSelectClinic = (value: string) => {
    setSelectedOrgId(value)
  }

  return (
    <Page alignItems="center">
      <VStack
        overflow="auto"
        p="24px"
        minHeight="100%"
        spacing="12"
        maxWidth="800px"
        width="100%"
        pb="200px"
      >
        <Center>
          <Logo maxWidth={150} />
        </Center>
        <Flex
          direction="column"
          gap="6"
          flex={1}
          shadow="2xl"
          borderRadius="md"
          p="12"
          mt="12"
          width="76%"
        >
          <Heading as="h2" alignSelf="center" size="lg">
            Link Your Organisation
          </Heading>
          <VStack spacing={2} alignItems="start" width="100%">
            <HStack>
              <Text fontWeight="bold">Find your HPI-O </Text>
              <ClickableTooltip label={HPIO_NUMBER_HINT}>
                <InfoIcon color="blue.500" />
              </ClickableTooltip>
            </HStack>
            <HStack spacing={4} width="100%">
              <Input
                bgColor="white"
                placeholder="Search organisations"
                value={hpioQuery}
                onChange={(e) => setHpioQuery(e.target.value)}
              />
              <Button
                size="sm"
                colorScheme="blue"
                onClick={fetchSearchResult}
                alignSelf="stretch"
                height="inherit"
                isLoading={searchLoading}
                minWidth={'54px'}
              >
                <HStack spacing={1}>
                  <Icon as={Search2Icon}></Icon>
                  <Text>Find</Text>
                </HStack>
              </Button>
            </HStack>
            {organizations ? (
              <VStack gap={1} mt={'8 !important'} alignItems="start" width={'100%'}>
                {organizations.length > 0 ? (
                  <>
                    <FormLabel as="legend" fontSize={'lg'}>
                      Available Organisations
                    </FormLabel>
                    <RadioGroup defaultValue="option1" width="100%" onChange={handleSelectClinic}>
                      <Stack
                        direction="column"
                        sx={{
                          '& label': {
                            bg: 'gray.100',
                            borderRadius: 'md',
                          },
                        }}
                      >
                        {organizations?.map((organization) => (
                          <Radio value={organization?.id} p="4" width="100%" key={organization?.id}>
                            {`${organization?.clinic.hpio_number}（${organization?.name}）`}
                          </Radio>
                        ))}
                      </Stack>
                    </RadioGroup>
                  </>
                ) : (
                  <Text alignSelf={'center'}>Sorry, no organisations found</Text>
                )}
                <Text textAlign={'center'} mt={'8 !important'}>
                  Don't see your organisation?{' '}
                  <Text
                    as="span"
                    color="blue.500"
                    cursor="pointer"
                    onClick={() =>
                      navigate(routes.createOrganization.path, {
                        state: { hpio: hpioQuery },
                      })
                    }
                  >
                    Click here
                  </Text>{' '}
                  to create a new organisation.
                </Text>
                <Button
                  colorScheme={'blue'}
                  width={'100%'}
                  mt="8 !important"
                  disabled={!selectedOrgId || linkLoading}
                  onClick={linkOrgToClinic}
                  isLoading={linkLoading}
                >
                  {linkLoading ? 'Joining...' : 'Join'}
                </Button>
              </VStack>
            ) : (
              searchCompleted && (
                <VStack width="100%" alignItems="start">
                  <Alert status="warning" rounded="md">
                    <AlertIcon />
                    <Text>HPI-O not found in RxPad. </Text>
                  </Alert>
                  <Text>
                    <RLink
                      to={routes.createClinic.path}
                      style={{ color: 'dodgerblue', fontWeight: 'bold' }}
                    >
                      Click Here
                    </RLink>{' '}
                    to manually create a new organisation.
                  </Text>
                  <Text
                    fontStyle="italic"
                    fontSize="sm"
                    color="gray.500"
                    style={{ marginTop: '30px' }}
                  >
                    <strong>DISCLAIMER: </strong>If you are an Individual Prescriber, please ensure
                    you have obtained consent from your nominated organisation before linking your
                    individual prescriber profile with the organisational HPI-O Number. If you are a
                    Clinic Owner / Practice Manager, please ensure your HPI-O Number is linked to
                    the RxPad CSP- otherwise, your clinicians will be unable to prescribe through
                    RxPad. Click{' '}
                    <Link
                      href="https://help.rxpad.com.au/en/articles/7923812-how-do-i-connect-my-clinic-s-hpi-o-to-rxpad-s-csp"
                      isExternal
                      target="_blank"
                      color={'blue.500'}
                      fontWeight={'bold'}
                    >
                      here
                    </Link>{' '}
                    for instructions on how to link your clinic's HPIO to our CSP.
                  </Text>
                </VStack>
              )
            )}
          </VStack>
        </Flex>
      </VStack>
    </Page>
  )
}
export default OrganizationLinkPage
